/**
 * Reportes del administrador: ingresos (abonos cobrados), producción (tratamientos realizados, por
 * odontólogo y por procedimiento) y saldos por cobrar. Todo en centavos, como el resto de la caja.
 */

export type Periodo = "hoy" | "semana" | "mes" | "mes_pasado" | "anio";
export const NOMBRE_PERIODO: Readonly<Record<Periodo, string>> = { hoy: "Hoy", semana: "Últimos 7 días", mes: "Este mes", mes_pasado: "Mes pasado", anio: "Este año" };

const iso = (d: Date) => d.toISOString().slice(0, 10);
const fecha = (s: string) => new Date(s + "T00:00:00Z");

/** Desde y hasta (incluidos) de un período, a partir de la fecha de hoy (aaaa-mm-dd). */
export function rangoPeriodo(p: Periodo, hoy: string): { desde: string; hasta: string } {
  const [a, m] = hoy.split("-").map(Number);
  switch (p) {
    case "hoy": return { desde: hoy, hasta: hoy };
    case "semana": return { desde: iso(new Date(fecha(hoy).getTime() - 6 * 86400000)), hasta: hoy };
    case "mes": return { desde: `${hoy.slice(0, 7)}-01`, hasta: hoy };
    case "mes_pasado": {
      const ini = new Date(Date.UTC(a, m - 2, 1)), fin = new Date(Date.UTC(a, m - 1, 0));
      return { desde: iso(ini), hasta: iso(fin) };
    }
    case "anio": return { desde: `${a}-01-01`, hasta: hoy };
  }
}

/** Un rango válido: fechas reales, desde ≤ hasta y no más de 3 años (para que la consulta no sea enorme). */
export function problemasRango(desde: string, hasta: string): string | null {
  const ok = (s: string) => /^\d{4}-\d{2}-\d{2}$/.test(s) && iso(fecha(s)) === s;
  if (!ok(desde) || !ok(hasta)) return "Elige fechas válidas.";
  if (desde > hasta) return "La fecha inicial debe ser anterior a la final.";
  if ((fecha(hasta).getTime() - fecha(desde).getTime()) / 86400000 > 366 * 3) return "Elige un período de hasta 3 años.";
  return null;
}

export interface AbonoReporte { fecha: string; monto: number; metodo: string; anulado: boolean; /** pago de una membresía del club (cuenta como ingreso de caja, no como abono al tratamiento) */ membresia?: boolean }
export interface RealizadoReporte { fecha: string; profesional: string; codigo: string; descripcion: string; cantidad: number; valor: number }
export interface CuentaReporte { pacienteId: string; paciente: string; hc: number; telefono: string; realizado: number; pagado: number; ultimoPago: string | null }

export interface Reporte {
  desde: string; hasta: string;
  ingresos: { /** de lo cobrado, lo que corresponde a membresías del club */ membresias: { total: number; cantidad: number }; total: number; cantidad: number; ticketPromedio: number; porMetodo: { metodo: string; total: number; cantidad: number }[]; serie: { etiqueta: string; desde: string; total: number }[]; agrupado: "dia" | "mes"; anulados: { cantidad: number; total: number } };
  produccion: { total: number; cantidad: number; porProfesional: { profesional: string; total: number; cantidad: number }[]; porProcedimiento: { codigo: string; descripcion: string; total: number; cantidad: number }[] };
  porCobrar: { total: number; pacientes: number; lista: (CuentaReporte & { saldo: number })[]; aFavor: number };
}

const MESES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

function agrupar<T>(xs: T[], clave: (x: T) => string, valor: (x: T) => number, cantidad: (x: T) => number = () => 1) {
  const m = new Map<string, { total: number; cantidad: number; muestra: T }>();
  for (const x of xs) {
    const k = clave(x), g = m.get(k) ?? { total: 0, cantidad: 0, muestra: x };
    g.total += valor(x); g.cantidad += cantidad(x); m.set(k, g);
  }
  return [...m.entries()].sort((a, b) => b[1].total - a[1].total || a[0].localeCompare(b[0]));
}

/** Serie de ingresos: por día si el período es de hasta 62 días; si no, por mes. Incluye los días sin cobros. */
function serie(abonos: AbonoReporte[], desde: string, hasta: string) {
  const dias = (fecha(hasta).getTime() - fecha(desde).getTime()) / 86400000 + 1;
  const porDia = dias <= 62;
  const out: { etiqueta: string; desde: string; total: number }[] = [];
  const idx = new Map<string, number>();
  if (porDia) {
    for (let t = fecha(desde).getTime(); t <= fecha(hasta).getTime(); t += 86400000) {
      const d = iso(new Date(t));
      idx.set(d, out.length);
      out.push({ etiqueta: `${Number(d.slice(8))} ${MESES[Number(d.slice(5, 7)) - 1]}`, desde: d, total: 0 });
    }
  } else {
    let [a, m] = desde.split("-").map(Number);
    const [ah, mh] = hasta.split("-").map(Number);
    while (a < ah || (a === ah && m <= mh)) {
      const k = `${a}-${String(m).padStart(2, "0")}`;
      idx.set(k, out.length);
      out.push({ etiqueta: `${MESES[m - 1]} ${String(a).slice(2)}`, desde: `${k}-01`, total: 0 });
      if (++m > 12) { m = 1; a++; }
    }
  }
  for (const x of abonos) {
    const i = idx.get(porDia ? x.fecha : x.fecha.slice(0, 7));
    if (i !== undefined) out[i].total += x.monto;
  }
  return { serie: out, agrupado: (porDia ? "dia" : "mes") as "dia" | "mes" };
}

export function armarReporte(o: { desde: string; hasta: string; abonos: AbonoReporte[]; realizados: RealizadoReporte[]; cuentas: CuentaReporte[] }): Reporte {
  const enRango = <T extends { fecha: string }>(xs: T[]) => xs.filter((x) => x.fecha >= o.desde && x.fecha <= o.hasta);
  const cobrados = enRango(o.abonos).filter((a) => !a.anulado);
  const anulados = enRango(o.abonos).filter((a) => a.anulado);
  const total = cobrados.reduce((s, a) => s + a.monto, 0);
  const hechos = enRango(o.realizados);
  const conSaldo = o.cuentas.map((c) => ({ ...c, saldo: c.realizado - c.pagado }));
  const deudores = conSaldo.filter((c) => c.saldo > 0).sort((a, b) => b.saldo - a.saldo);
  return {
    desde: o.desde, hasta: o.hasta,
    ingresos: {
      membresias: { total: cobrados.filter((a) => a.membresia).reduce((s, a) => s + a.monto, 0), cantidad: cobrados.filter((a) => a.membresia).length },
      total, cantidad: cobrados.length, ticketPromedio: cobrados.length ? Math.round(total / cobrados.length) : 0,
      porMetodo: agrupar(cobrados, (a) => a.metodo, (a) => a.monto).map(([metodo, g]) => ({ metodo, total: g.total, cantidad: g.cantidad })),
      ...serie(cobrados, o.desde, o.hasta),
      anulados: { cantidad: anulados.length, total: anulados.reduce((s, a) => s + a.monto, 0) },
    },
    produccion: {
      total: hechos.reduce((s, x) => s + x.valor, 0), cantidad: hechos.reduce((s, x) => s + x.cantidad, 0),
      porProfesional: agrupar(hechos, (x) => x.profesional, (x) => x.valor, (x) => x.cantidad).map(([profesional, g]) => ({ profesional, total: g.total, cantidad: g.cantidad })),
      porProcedimiento: agrupar(hechos, (x) => x.codigo, (x) => x.valor, (x) => x.cantidad).slice(0, 12)
        .map(([codigo, g]) => ({ codigo, descripcion: g.muestra.descripcion, total: g.total, cantidad: g.cantidad })),
    },
    porCobrar: {
      total: deudores.reduce((s, c) => s + c.saldo, 0), pacientes: deudores.length, lista: deudores.slice(0, 50),
      aFavor: conSaldo.filter((c) => c.saldo < 0).reduce((s, c) => s - c.saldo, 0),
    },
  };
}

/** CSV (separado por punto y coma, como lo abre Excel en español) de un reporte. */
export function reporteCSV(r: Reporte, dinero: (c: number) => string): string {
  const q = (s: string | number) => { const t = String(s); return /[;"\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t; };
  const filas: (string | number)[][] = [
    [`Reporte del ${r.desde} al ${r.hasta}`], [],
    ["INGRESOS"], ["Total cobrado", dinero(r.ingresos.total)], ["Abonos y membresías", r.ingresos.cantidad], ["De ellos, membresías del club", r.ingresos.membresias.cantidad, dinero(r.ingresos.membresias.total)], ["Anulados", r.ingresos.anulados.cantidad, dinero(r.ingresos.anulados.total)], [],
    ["Forma de pago", "Abonos", "Total"], ...r.ingresos.porMetodo.map((m) => [m.metodo, m.cantidad, dinero(m.total)]), [],
    [r.ingresos.agrupado === "dia" ? "Día" : "Mes", "Total"], ...r.ingresos.serie.map((s) => [s.desde, dinero(s.total)]), [],
    ["PRODUCCIÓN (tratamientos realizados)"], ["Total", dinero(r.produccion.total)], [],
    ["Profesional", "Procedimientos", "Total"], ...r.produccion.porProfesional.map((p) => [p.profesional, p.cantidad, dinero(p.total)]), [],
    ["Código", "Procedimiento", "Cantidad", "Total"], ...r.produccion.porProcedimiento.map((p) => [p.codigo, p.descripcion, p.cantidad, dinero(p.total)]), [],
    ["POR COBRAR (realizado y no pagado, a hoy)"], ["Total", dinero(r.porCobrar.total)], [],
    ["HC", "Paciente", "Celular", "Realizado", "Pagado", "Saldo", "Último pago"],
    ...r.porCobrar.lista.map((c) => [c.hc, c.paciente, c.telefono, dinero(c.realizado), dinero(c.pagado), dinero(c.saldo), c.ultimoPago ?? ""]),
  ];
  return "﻿" + filas.map((f) => f.map(q).join(";")).join("\r\n");
}
